import { zoomLevelStore } from './store/zoomLevel';
import { getDeletesForSegment } from './playerConfigUtil';
import { getSegmentsTrack } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/utils';
import { CompositeSegment } from '@/App/remotion/MainPlayer/types/Segment/CompositeSegment';
import { Segment } from '@/App/remotion/MainPlayer/types/Segment/Segment';
import { SegmentType } from '@/App/remotion/MainPlayer/types/Segment/Type';
import { Clip, ClipDeletes } from '@/domains/asset';
import { getClipFPS } from '@/libs/clipUtils';
import { roundToNDecimalPlaces } from '@/libs/utils';
import { approxGTE, approxLT, approxLTE } from '@/libs/compare';
import { isSplitsInTimelineEnabled } from '@/stores/clip';
import { VideoSegment } from '@/App/remotion/MainPlayer/types/Segment/VideoSegment';

// Minimum width for intro, outro segment
export const MIN_SEGMENT_WIDTH = 24;

// Minimum duration for a video segment in seconds
export const MIN_VIDEO_SEGMENT_DURATION = 3;

// !!TODO @HiteshChoudhary0098 need to sort segments in a more efficient way need to store the values for sorted deletes

/**
 * Time Nomenclature Documentation
 *
 * This file uses specific terminology for different time representations:
 *
 * - **absoluteTime**: Time in the absolute timeline - represents the time as seen in the timeline this includes all the deleted in the composite segment
 *
 * - **playerTime**: This is the time as seen in the actual player this is the time that is used to play the clip
 *
 * - **segmentTime**: Time within a specific segment - includes the effects of deletes and trim operations applied to that particular segment
 *
 */

/**
 * Calculates the total deleted time from the beginning of the segment up to a specific time point.
 * This function accounts for deleted segments within the segment's bounds.
 *
 * @param playerTime
 * @param deletes - Object containing all deleted time ranges in the clip
 * @param segment - The segment containing the time bounds
 * @param fps - Frames per second, defaults to FPS_24
 * @returns The total duration of deleted segments up to the specified player time
 */
export const getSegmentDeleteTimeTillPlayerTime = (
  playerTime: number,
  deletes: ClipDeletes,
  segment: CompositeSegment | VideoSegment,
  fps: number
) => {
  const startTrimOffset = (segment.currentBounds?.fromFrame || 0) / fps;
  const endTrimOffset = (segment.currentBounds?.toFrame || 0) / fps;
  const segmentStartTime = (segment?.fromFrame || 0) / fps;

  const deleteTime = Object.entries(deletes)
    .sort((itemA, itemB) => itemA[1].bounds[0] - itemB[1].bounds[0])
    .reduce((acc, [_, range]) => {
      if (
        range.bounds[0] !== 0 &&
        roundToNDecimalPlaces(range.bounds[0], 3) !== roundToNDecimalPlaces(endTrimOffset, 3) &&
        range.bounds[0] < endTrimOffset &&
        range.bounds[0] <= playerTime - segmentStartTime + acc + startTrimOffset
      ) {
        return acc + (range.bounds[1] - range.bounds[0]);
      }
      return acc;
    }, 0);
  return deleteTime;
};

/**
 * Converts absolute time to player time for that segment.
 * This function handles the conversion between absolute time (including deleted segments) and the actual time within the composite segment.
 *
 * @param remainingAbsoluteTime - The time after removing any previous elements absolute times and includes deleted segments (time with added delete time but not the trim timing)
 * @param deletes - Object containing all deleted time ranges in the clip
 * @param compositeSegment - The composite segment containing the time bounds
 * @returns An object containing:
 *   - segmentTime: The time within the composite segment after accounting for deleted segments
 *   - timeExceeded: The time exceeded beyond the composite segment bounds
 */
const getSegmentPlayerTimeFromRemainingAbsoluteTimeForVideoAndCompositeSegments = (
  remainingAbsoluteTime: number,
  deletes: ClipDeletes,
  segment: CompositeSegment | VideoSegment,
  fps: number
) => {
  const startOffset = (segment.currentBounds?.fromFrame || 0) / fps;
  const maxBound = (segment.currentBounds?.toFrame || 0) / fps;
  const sortedDeletes = Object.entries(deletes)
    .sort((a, b) => a[1].bounds[0] - b[1].bounds[0])
    .filter(([_, range]) => approxLTE(range.bounds[1], maxBound));
  const segmentTime = remainingAbsoluteTime + startOffset;
  let deletedTime = 0;
  for (const [_, range] of sortedDeletes) {
    if (approxLTE(range.bounds[1], segmentTime)) {
      deletedTime += range.bounds[1] - range.bounds[0];
    } else if (approxLTE(range.bounds[0], segmentTime)) {
      deletedTime += segmentTime - range.bounds[0];
    }
  }
  const segmentTimeWithoutTrimAndDeletedTime = Math.min(
    Math.max(0, segmentTime - deletedTime),
    (segment.toFrame - segment.fromFrame) / fps
  );

  return {
    segmentTime: segmentTimeWithoutTrimAndDeletedTime,
    timeExceeded: segmentTime - segmentTimeWithoutTrimAndDeletedTime - deletedTime
  };
};

/**
 * Converts absolute player time to segment time by removing the deleted time.
 * This function handles both composite and non-composite segments.
 * This function is only be called with remaining absolute time that is greater than 0.
 *
 * @param remainingAbsoluteTime - The remaining absolute time that includes deleted segments
 * @param segment - The segment to calculate time for (can be any segment type)
 * @param clipData - The clip data containing metadata and deletes information
 * @returns An object containing:
 *   - segmentTime: The time within the segment after accounting for deleted segments
 *   - timeExceeded: The time exceeded beyond the segment bounds
 */
export const getSegmentPlayerTimeFromRemainingAbsoluteTime = (
  remainingAbsoluteTime: number,
  segment: Segment,
  clipData: Clip,
  minSegmentWidth = MIN_SEGMENT_WIDTH
) => {
  const fps = getClipFPS(clipData);
  if (segment.type === SegmentType.COMPOSITE) {
    const deletes = getDeletesForSegment(clipData, segment.id);
    return getSegmentPlayerTimeFromRemainingAbsoluteTimeForVideoAndCompositeSegments(
      remainingAbsoluteTime,
      deletes,
      segment,
      fps
    );
  }
  const zoomLevel = zoomLevelStore.getSnapshot().zoomLevel;
  const isSplitsEnabled = isSplitsInTimelineEnabled(clipData);
  if (isSplitsEnabled && segment.type === SegmentType.VIDEO) {
    const segmentTrimStartTime = segment.currentBounds?.fromFrame || 0;
    const segmentTrimEndTime = segment.currentBounds?.toFrame || 0;
    const segmentTotalLength = (segmentTrimEndTime - segmentTrimStartTime) * zoomLevel;
    // When the segment is less than the min segment width, we need to adjust for scaling
    if (approxLTE(segmentTotalLength, minSegmentWidth)) {
      const totalSegmentTime = (segment.toFrame - segment.fromFrame) / fps;
      const absoluteTimeWidthOfSegment = minSegmentWidth / zoomLevel / fps;
      // When the absolute time is greater than the absolute time width of the segment, we need to adjust for scaling
      if (approxGTE(remainingAbsoluteTime, absoluteTimeWidthOfSegment)) {
        return {
          segmentTime: totalSegmentTime,
          timeExceeded: remainingAbsoluteTime - absoluteTimeWidthOfSegment
        };
      }
      // When the absolute time is smaller than the absolute time width of the segment, we need to adjust for scaling and for deletes
      const absoluteTimeAdjustedForMinSegmentWidth = remainingAbsoluteTime / (minSegmentWidth / segmentTotalLength);
      return getSegmentPlayerTimeFromRemainingAbsoluteTimeForVideoAndCompositeSegments(
        absoluteTimeAdjustedForMinSegmentWidth,
        segment.segment_metadata?.deletes || {},
        segment,
        fps
      );
    }
    return getSegmentPlayerTimeFromRemainingAbsoluteTimeForVideoAndCompositeSegments(
      remainingAbsoluteTime,
      segment.segment_metadata?.deletes || {},
      segment,
      fps
    );
  }
  const segmentTotalLength = (segment.toFrame - segment.fromFrame) * zoomLevel;
  if (approxLTE(segmentTotalLength, minSegmentWidth)) {
    const totalSegmentTime = (segment.toFrame - segment.fromFrame) / fps;
    const absoluteTimeWidthOfSegment = minSegmentWidth / zoomLevel / fps;
    if (approxGTE(remainingAbsoluteTime, absoluteTimeWidthOfSegment)) {
      return {
        segmentTime: totalSegmentTime,
        timeExceeded: remainingAbsoluteTime - absoluteTimeWidthOfSegment
      };
    }
    const segmentTime = (remainingAbsoluteTime * totalSegmentTime) / absoluteTimeWidthOfSegment;
    return {
      segmentTime,
      timeExceeded: 0
    };
  }

  const segmentTime = Math.min((segment.toFrame - segment.fromFrame) / fps, remainingAbsoluteTime);

  return {
    segmentTime,
    timeExceeded: remainingAbsoluteTime - segmentTime
  };
};

export const getSegmentPlayerTimeFromAbsoluteTime = (
  absoluteTime: number,
  segment: Segment,
  clipData: Clip,
  minSegmentWidth = MIN_SEGMENT_WIDTH
) => {
  const remainingAbsoluteTime = absoluteTime - getPreviousSegmentAbsoluteTime(segment, clipData);
  return getSegmentPlayerTimeFromRemainingAbsoluteTime(remainingAbsoluteTime, segment, clipData, minSegmentWidth);
};

/**
 * Converts player time to absolute time by adding the deleted time.
 * This function calculates the absolute time that includes all deleted segments.
 *
 * @param time - The player time (time without deleted segments)
 * @param clipData - The clip data containing metadata and player configuration
 * @param minSegmentWidth - The minimum width (in pixels) that a segment should have in the timeline view.
 *                          Passing -Infinity disables the minimum segment width constraint, allowing segments
 *                          to have their actual calculated width without any minimum size enforcement.
 * @param overrideZoomLevel - The zoom level to use for the calculation. If not provided, the current zoom level is used.
 * @returns The absolute time that includes all deleted segments
 */
export const getAbsolutePlayerTime = (
  time: number,
  clipData: Clip,
  minSegmentWidth = MIN_SEGMENT_WIDTH,
  overrideZoomLevel?: number
) => {
  const playerConfig = clipData.asset_metadata.player_config;
  const zoomLevel = overrideZoomLevel || zoomLevelStore.getSnapshot().zoomLevel;
  const isSplitsEnabled = isSplitsInTimelineEnabled(clipData);

  if (!playerConfig) {
    return 0;
  }
  const fps = getClipFPS(clipData);

  const segmentsTrack = getSegmentsTrack(playerConfig);
  const sortedSegments = [...segmentsTrack.segments].sort((a, b) => a.fromFrame - b.fromFrame);
  const totalAdditionalTime = sortedSegments.reduce((additionalTime, segment) => {
    const currentSegmentStartTime = segment.fromFrame / fps;
    if (approxGTE(currentSegmentStartTime, time)) {
      return additionalTime;
    }

    if (segment.type === SegmentType.COMPOSITE) {
      //  Not supporting min width for composite segments yet
      return (
        additionalTime +
        getSegmentDeleteTimeTillPlayerTime(time, getDeletesForSegment(clipData, segment.id), segment, fps)
      );
    }

    if (segment.type === SegmentType.VIDEO && isSplitsEnabled) {
      const totalSegmentTimeLength =
        ((segment.currentBounds?.toFrame || 0) - (segment.currentBounds?.fromFrame || 0)) * zoomLevel;
      const totalSegmentTime = (segment.toFrame - segment.fromFrame) / fps;

      // When the segment is larger than the min segment width, we can just return the deleted time
      if (approxGTE(totalSegmentTimeLength, minSegmentWidth)) {
        return (
          additionalTime +
          getSegmentDeleteTimeTillPlayerTime(time, segment.segment_metadata?.deletes || {}, segment, fps)
        );
      }

      // When the segment is smaller than the min segment but the time exceeds this segment
      if (approxLTE(totalSegmentTime, time - currentSegmentStartTime)) {
        return additionalTime + (minSegmentWidth / zoomLevel / fps - totalSegmentTime);
      }

      // When the segment is smaller than the min segment but the time does not exceed this segment we need to adjust for scaling and for deletes
      return (
        additionalTime +
        (time - currentSegmentStartTime) * (minSegmentWidth / totalSegmentTimeLength - 1) +
        getSegmentDeleteTimeTillPlayerTime(time, segment.segment_metadata?.deletes || {}, segment, fps) *
          (minSegmentWidth / totalSegmentTimeLength)
      );
    }

    const totalSegmentTime = (segment.toFrame - segment.fromFrame) / fps;
    const isSegmentSizeSmallerThanMinSegmentWidth = approxLTE(totalSegmentTime * fps * zoomLevel, minSegmentWidth);

    if (isSegmentSizeSmallerThanMinSegmentWidth) {
      if (approxLTE(totalSegmentTime, time - currentSegmentStartTime)) {
        return additionalTime + minSegmentWidth / (fps * zoomLevel) - totalSegmentTime;
      }

      return (
        additionalTime + (minSegmentWidth / (fps * zoomLevel) / totalSegmentTime - 1) * (time - currentSegmentStartTime)
      );
    }

    return additionalTime;
  }, 0);

  return time + totalAdditionalTime;
};

/**
 * Converts absolute time to player time by removing the deleted time.
 * This function handles the conversion from absolute time (including deleted segments) to player time.
 *
 * @param absoluteTime - The absolute time that includes all deleted segments
 * @param clipData - The clip data containing metadata and player configuration
 * @param minSegmentWidth - The minimum width (in pixels) that a segment should have in the timeline view.
 *                          Passing -Infinity disables the minimum segment width constraint, allowing segments
 *                          to have their actual calculated width without any minimum size enforcement.
 * @returns The player time after removing all deleted segments
 */
export const getPlayerTimeFromAbsoluteTime = (
  absoluteTime: number,
  clipData: Clip,
  minSegmentWidth = MIN_SEGMENT_WIDTH
) => {
  const playerConfig = clipData.asset_metadata.player_config;

  if (!playerConfig) {
    return 0;
  }

  const segmentsTrack = getSegmentsTrack(playerConfig);
  if (!segmentsTrack || !segmentsTrack.segments.length) {
    return absoluteTime;
  }

  let playerTime = 0;
  let remainingAbsoluteTime = absoluteTime;
  const sortedSegments = [...segmentsTrack.segments].sort((a, b) => a.fromFrame - b.fromFrame);
  for (const segment of sortedSegments) {
    if (approxLTE(remainingAbsoluteTime, 0)) {
      break;
    }

    const { segmentTime, timeExceeded } = getSegmentPlayerTimeFromRemainingAbsoluteTime(
      remainingAbsoluteTime,
      segment,
      clipData,
      minSegmentWidth
    );

    playerTime += segmentTime;
    remainingAbsoluteTime = timeExceeded;
  }

  return playerTime;
};

/**
 * Calculates the total absolute time of all segments that come before the specified segment.
 * This function iterates through all segments in the player configuration and sums up
 * the duration of segments that appear before the target segment in the timeline.
 *
 * @param segment - The target segment to calculate previous segments' time for
 * @param clipData - The clip data containing metadata and player configuration
 * @param minSegmentWidth - The minimum width (in pixels) that a segment should have in the timeline view.
 *                          Passing -Infinity disables the minimum segment width constraint, allowing segments
 *                          to have their actual calculated width without any minimum size enforcement.
 * @returns The total absolute time (in seconds) of all segments before the target segment
 */
export const getPreviousSegmentAbsoluteTime = (
  segment: Segment,
  clipData: Clip,
  minSegmentWidth = MIN_SEGMENT_WIDTH
) => {
  const playerConfig = clipData.asset_metadata.player_config;

  if (!playerConfig) {
    return 0;
  }
  const fps = getClipFPS(clipData);

  const segmentTrack = getSegmentsTrack(playerConfig);
  let previousSegmentTime = 0;
  const zoomLevel = zoomLevelStore.getSnapshot().zoomLevel;
  const sortedSegments = [...segmentTrack.segments].sort((a, b) => a.fromFrame - b.fromFrame);
  for (const currentSegment of sortedSegments) {
    if (currentSegment.id === segment.id) {
      return previousSegmentTime;
    }
    if (currentSegment.type === SegmentType.COMPOSITE) {
      previousSegmentTime += (currentSegment.currentBounds.toFrame - currentSegment.currentBounds.fromFrame) / fps;
    } else {
      previousSegmentTime += Math.max(
        (currentSegment.toFrame - currentSegment.fromFrame) / fps,
        minSegmentWidth / fps / zoomLevel
      );
    }
  }
  return previousSegmentTime;
};

/**
 * Calculates the total time of all segments that come before the specified segment.
 * This function iterates through all segments in the player configuration and sums up
 * the duration of segments that appear before the target segment in the timeline.
 *
 * @param segment - The target segment to calculate previous segments' time for
 * @param clipData - The clip data containing metadata and player configuration
 * @returns The total time of all segments before the target segment
 */
export const getPreviousSegmentTime = (segment: Segment, clipData: Clip) => {
  const playerConfig = clipData.asset_metadata.player_config;

  if (!playerConfig) {
    return 0;
  }
  const fps = getClipFPS(clipData);

  const segmentTrack = getSegmentsTrack(playerConfig);
  let previousSegmentTime = 0;
  segmentTrack.segments.some(currentSegment => {
    if (currentSegment.id === segment.id) {
      return true;
    }
    previousSegmentTime += (currentSegment.toFrame - currentSegment.fromFrame) / fps;
    return false;
  });
  return previousSegmentTime;
};

export const isFallingBetweenRange = (
  ranges: { start: number; end: number }[],
  k: number,
  includeBoundaries = false
) => {
  return ranges.some(({ start, end }) => {
    if (includeBoundaries) {
      return start <= k && k <= end;
    }
    return start < k && k < end;
  });
};

/**
 * Finds the segment that contains the current frame, based on the current time and clip data.
 *
 * @param {number} currentTime - The current time in seconds (player time).
 * @param {Clip} clipData - The clip data containing metadata and player configuration.
 * @returns {Segment | null} The segment at the current frame, or null if none is found or player config is missing.
 */
export const getSegmentAtCurrentFrame = (currentTime: number, clipData: Clip) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return null;
  }
  const fps = getClipFPS(clipData);
  const frame = currentTime * fps;

  const segmentsTrack = getSegmentsTrack(playerConfig);
  const segment = segmentsTrack.segments.find(
    segment => approxLTE(segment.fromFrame, frame) && approxLT(frame, segment.toFrame)
  );
  return segment || null;
};

export const getSegmentTimeFromPlayerTime = (playerTime: number, clipData: Clip, segmentId: string) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return 0;
  }
  const segmentTrack = getSegmentsTrack(playerConfig);
  const segment = segmentTrack.segments.find(segment => segment.id === segmentId);
  if (!segment) {
    return 0;
  }

  const fps = getClipFPS(clipData);

  const absolutePlayerTime = getAbsolutePlayerTime(playerTime, clipData, -Infinity);
  const previousSegmentAbsoluteTime = getPreviousSegmentAbsoluteTime(segment, clipData, -Infinity);
  const isSplitsEnabled = isSplitsInTimelineEnabled(clipData);
  if ((segment.type === SegmentType.VIDEO && isSplitsEnabled) || segment.type === SegmentType.COMPOSITE) {
    return absolutePlayerTime - previousSegmentAbsoluteTime + (segment.currentBounds?.fromFrame || 0) / fps;
  }
  return absolutePlayerTime - previousSegmentAbsoluteTime;
};

export const getPlayerTimeFromSegmentTime = (segmentTime: number, clipData: Clip, segmentId: string) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return 0;
  }
  const segmentTrack = getSegmentsTrack(playerConfig);
  const segment = segmentTrack.segments.find(segment => segment.id === segmentId);
  if (!segment) {
    return 0;
  }
  const fps = getClipFPS(clipData);

  const isSplitsEnabled = isSplitsInTimelineEnabled(clipData);

  if (
    (!isSplitsEnabled && segment.type === SegmentType.VIDEO) ||
    (segment.type !== SegmentType.COMPOSITE && segment.type !== SegmentType.VIDEO)
  ) {
    return segment.fromFrame / fps + segmentTime;
  }
  const startTime = segment.fromFrame / fps;
  const allDeletes = getDeletesForSegment(clipData, segment.id);
  let deletedTime = 0;
  for (const [_, range] of Object.entries(allDeletes)) {
    if (approxLTE(range.bounds[1], segmentTime)) {
      deletedTime += range.bounds[1] - range.bounds[0];
    } else if (approxLTE(range.bounds[0], segmentTime)) {
      deletedTime += segmentTime - range.bounds[0];
    }
  }
  return startTime + segmentTime - deletedTime;
};

export const getAbsoluteTimeFromSegmentTime = (
  segmentTime: number,
  clipData: Clip,
  segmentId: string,
  minSegmentWidth = MIN_SEGMENT_WIDTH
) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return 0;
  }
  const segmentTrack = getSegmentsTrack(playerConfig);
  const segment = segmentTrack.segments.find(segment => segment.id === segmentId);
  if (!segment) {
    return 0;
  }
  const startAbsoluteTime = getPreviousSegmentAbsoluteTime(segment, clipData, minSegmentWidth);

  const isSplitsEnabled = isSplitsInTimelineEnabled(clipData);

  if (
    (!isSplitsEnabled && segment.type !== SegmentType.COMPOSITE) ||
    (segment.type !== SegmentType.VIDEO && segment.type !== SegmentType.COMPOSITE)
  ) {
    return startAbsoluteTime + segmentTime;
  }
  const fps = getClipFPS(clipData);
  const trimOffset = (segment?.currentBounds?.fromFrame || 0) / fps;
  const segmentTotalTime = ((segment.currentBounds?.toFrame || 0) - (segment.currentBounds?.fromFrame || 0)) / fps;
  if (segment.type === SegmentType.VIDEO) {
    const segmentTotalSize = segmentTotalTime * fps * zoomLevelStore.getSnapshot().zoomLevel;
    if (approxLTE(segmentTotalSize, minSegmentWidth)) {
      return (
        startAbsoluteTime + (Math.min(segmentTime - trimOffset, segmentTotalTime) * minSegmentWidth) / segmentTotalSize
      );
    }
  }
  return startAbsoluteTime + Math.max(0, Math.min(segmentTime - trimOffset, segmentTotalTime));
};

export const getSegmentTimeFromAbsoluteTime = (
  absoluteTime: number,
  clipData: Clip,
  segmentId: string,
  minSegmentWidth = MIN_SEGMENT_WIDTH
) => {
  const playerConfig = clipData.asset_metadata.player_config;
  if (!playerConfig) {
    return 0;
  }
  const segmentTrack = getSegmentsTrack(playerConfig);
  const segment = segmentTrack.segments.find(segment => segment.id === segmentId);
  if (!segment) {
    return 0;
  }
  const fps = getClipFPS(clipData);

  const isSplitsEnabled = isSplitsInTimelineEnabled(clipData);

  if (
    (!isSplitsEnabled && segment.type !== SegmentType.COMPOSITE) ||
    (segment.type !== SegmentType.VIDEO && segment.type !== SegmentType.COMPOSITE)
  ) {
    return absoluteTime - getPreviousSegmentAbsoluteTime(segment, clipData);
  }

  const trimOffset = (segment?.currentBounds?.fromFrame || 0) / fps;
  const segmentTotalAbsoluteTime =
    ((segment.currentBounds?.toFrame || 0) - (segment.currentBounds?.fromFrame || 0)) / fps;

  if (segment.type === SegmentType.VIDEO) {
    const segmentTotalSize = segmentTotalAbsoluteTime * fps * zoomLevelStore.getSnapshot().zoomLevel;
    if (approxLTE(segmentTotalSize, minSegmentWidth)) {
      return (
        Math.min(
          segmentTotalAbsoluteTime,
          (absoluteTime - getPreviousSegmentAbsoluteTime(segment, clipData)) * (segmentTotalSize / minSegmentWidth)
        ) + trimOffset
      );
    }
  }

  return (
    Math.min(segmentTotalAbsoluteTime, absoluteTime - getPreviousSegmentAbsoluteTime(segment, clipData)) + trimOffset
  );
};
